/*
* Copyright (c) 2009-2021. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/
import VueRouter from 'vue-router';

import ViewerLoading from './components/utils/ViewerLoading.vue';

function lazyRoute(loader) {
  return () => ({
    component: loader(),
    delay: 120,
    timeout: 60000
  });
}

function lazyViewerRoute(loader) {
  return () => ({
    component: loader(),
    loading: ViewerLoading,
    delay: 80,
    timeout: 60000
  });
}

// Lazy-loaded route components to reduce initial JS payload.
const GlobalDashboard = lazyRoute(() => import('./components/GlobalDashboard.vue'));
const ListProjects = lazyRoute(() => import('./components/project/ListProjects.vue'));
const CytomineStorage = lazyRoute(() => import('./components/storage/CytomineStorage.vue'));
const ListOntologies = lazyRoute(() => import('./components/ontology/ListOntologies.vue'));
const ListSoftware = lazyRoute(() => import('./components/software/ListSoftware.vue'));
const ListTraining = lazyRoute(() => import('./components/software/ListTraining.vue'));
const ListImages = lazyRoute(() => import('./components/image/ListImages.vue'));
const ListDatasets = lazyRoute(() => import('./components/dataset/ListDatasets.vue'));
const DatasetWorkspace = lazyRoute(() => import('./components/dataset/DatasetWorkspace.vue'));
const ImageInformation = lazyRoute(() => import('./components/image/ImageInformation.vue'));
const ListAnnotations = lazyRoute(() => import('./components/annotations/ListAnnotations.vue'));
const ProjectActivity = lazyRoute(() => import('./components/project/ProjectActivity.vue'));
const Analysis = lazyRoute(() => import('./components/job/Analysis.vue'));
const Training = lazyRoute(() => import('./components/job/Training.vue'));
const AnalysisRunResults = lazyRoute(() => import('./components/job/AnalysisRunResults.vue'));
const ProjectInformation = lazyRoute(() => import('./components/project/ProjectInformation.vue'));
const ProjectConfiguration = lazyRoute(() => import('./components/project/ProjectConfiguration.vue'));
const Account = lazyRoute(() => import('./components/user/Account.vue'));
const AdvancedSearch = lazyRoute(() => import('./components/search/AdvancedSearch.vue'));
const CytomineViewer = lazyViewerRoute(() => import(/* webpackChunkName: "viewer-entry" */ './components/viewer/CytomineViewer.vue'));
const CytomineProject = lazyRoute(() => import('./components/project/CytomineProject.vue'));
const ProjectHome = lazyRoute(() => import('./components/project/ProjectHome.vue'));
const MemberActivityDetails = lazyRoute(() => import('./components/project/activity/MemberActivityDetails.vue'));
const AdminPanel = lazyRoute(() => import('./components/admin/AdminPanel.vue'));
const UserActivity = lazyRoute(() => import('./components/user/UserActivity.vue'));
const PageNotFound = lazyRoute(() => import('./components/PageNotFound.vue'));
const SoftwareInformation = lazyRoute(() => import('./components/software/SoftwareInformation.vue'));

// Define routes
const routes = [
  {
    path: '/recent',
    component: GlobalDashboard, // Alternative: component: require("./components/GlobalDashboard.vue").default
  },
  {
    path: '/projects',
    component: ListProjects,
  },
  {
    path: '/storage',
    component: CytomineStorage,
  },
  {
    path: '/ontology/:idOntology?',
    component: ListOntologies
  },
  {
    path: '/software',
    component: ListSoftware,
    props: {
      isTrain: false,
    }
  },
  {
    path: '/software/:idSoftware',
    component: SoftwareInformation
  },
  {
    path: '/training',
    component: ListTraining,
  },
  {
    path: '/advanced-search/:searchString?',
    component: AdvancedSearch
  },
  {
    path: '/account',
    component: Account,
  },
  {
    path: '/project/:idProject',
    component: CytomineProject,
    children: [
      {
        path: '',
        component: ProjectHome
      },
      {
        path: 'images',
        component: ListImages
      },
      {
        path: 'datasets',
        component: ListDatasets
      },
      {
        path: 'datasets/:idDataset',
        component: DatasetWorkspace
      },
      {
        path: 'image/:idImages',
        component: CytomineViewer
      },
      {
        path: 'image/:idImages/slice/:idSlices',
        component: CytomineViewer
      },
      {
        path: 'image/:idImage/information',
        component: ImageInformation
      },
      {
        path: 'image/:idImages/annotation/:idAnnotation',
        component: CytomineViewer
      },
      {
        path: 'image/:idImages/slice/:idSlices/annotation/:idAnnotation',
        component: CytomineViewer
      },
      {
        path: 'annotations',
        component: ListAnnotations
      },
      {
        path: 'analysis/:idJob',
        component: AnalysisRunResults
      },
      {
        path: 'analysis',
        component: Analysis
      },
      {
        path: 'training',
        component: Training
      },
      {
        path: 'activity',
        component: ProjectActivity
      },
      {
        path: 'activity/user/:idUser',
        component: MemberActivityDetails
      },
      {
        path: 'information',
        component: ProjectInformation
      },
      {
        path: 'configuration',
        component: ProjectConfiguration
      },
      {
        path: '*',
        component: PageNotFound
      }
    ]
  },
  {
    path: '/activity',
    component: UserActivity
  },
  {
    path: '/admin',
    component: AdminPanel
  },

  // redirections for old URLS
  {path: '/', redirect: '/projects'},
  {path: '/userdashboard', redirect: '/'},
  {path: '/project', redirect: '/projects'},
  {path: '/explorer', redirect: '/'},
  {path: '/upload', redirect: '/storage'},

  {path: '/activity', redirect: '/'},
  {path: '/activity-:idProject-', redirect: '/project/:idProject/activity'},
  {path: '/activity-:idProject-:idUser', redirect: '/project/:idProject/activity/user/:idUser'},

  {path: '/search-', redirect: '/advanced-search'},

  {path: '/admin-tabs-dashboard', redirect: '/admin?tab=dashboard'},
  {path: '/admin-tabs-users', redirect: '/admin?tab=users'},
  {path: '/admin-tabs-groups', redirect: '/'}, // TODO
  {path: '/admin-tabs-permissions', redirect: '/'}, // TODO
  {path: '/admin-tabs-configuration', redirect: '/admin?tab=configuration'},
  {path: '/admin-tabs-user-activation', redirect: '/admin?tab=user-activation'},

  {path: '/tabs-dashboard-:idProject', redirect: '/project/:idProject/information'},
  {path: '/tabs-images-:idProject', redirect: '/project/:idProject/images'},
  {path: '/tabs-annotations-:idProject', redirect: '/project/:idProject/annotations'},
  {path: '/tabs-annotationproperties-:idProject-:idAnnot', redirect: '/project/:idProject'},
  {path: '/tabs-imageproperties-:idProject-:idImage', redirect: '/project/:idProject'},
  {path: '/tabs-imageproperties-:idProject-:idImage', redirect: '/project/:idProject'},
  {path: '/tabs-algos-:idProject', redirect: '/project/:idProject/analysis'},
  {path: '/tabs-config-:idProject', redirect: '/project/:idProject/configuration'},
  {path: '/tabs-usersconfig-:idProject', redirect: '/project/:idProject/configuration?tab=members'},
  {path: '/tabs-#tabs-useractivity-:idProject-:idUser', redirect: '/project/:idProject/activity/user/:idUser'},
  {path: '/tabs-image-:idProject-:idImage-0', redirect: '/project/:idProject/image/:idImage'},
  {path: '/tabs-image-:idProject-:idImage-:idAnnotation', redirect: '/project/:idProject/image/:idImage/annotation/:idAnnotation'},
  {path: '/tabs-image-:idProject-:idImage-', redirect: '/project/:idProject/image/:idImage'},
  // -----

  {
    path: '*',
    component: PageNotFound
  }
];

// Create router instance
const router = new VueRouter({
  routes: routes,
  linkActiveClass: 'is-active'
});

export default router;
